import { theme } from 'antd';
import { CSSProperties } from 'react';
import { UIElementTypeColors } from '#types/System.Themes.types';

const { useToken } = theme;

/** Defines color schemes for different element states */
interface EditorColors {
    /** Border color in normal state */
    border: string;
    /** Background color in normal state */
    background: string;
    /** Border color when selected */
    selectedBorder: string;
    /** Background color when selected */
    selectedBackground: string;
}

/** Props for generating element styles */
interface StyleProps {
    /** Whether the element is a container (Grid, Row, Column) */
    isContainer: boolean;
    /** Whether the element is a Column */
    isColumn: boolean;
    /** Whether the element is selected */
    isSelected: boolean;
    /** Whether the element is a meta field */
    isMeta: boolean;
    /** Whether the element is a field */
    isField: boolean;
    /** Whether the element is a form property */
    isFormProp?: boolean;
    /** Whether the element is an attachments element */
    isAttachments?: boolean;
    /** Additional style overrides */
    style?: CSSProperties;
    /** Element-specific style props */
    elementProps?: { style?: CSSProperties };
}

/** Hook providing style functions for LayoutEditorElement */
export const useLayoutEditorStyles = () => {
    const { token } = useToken();
    const opacity = '25';

    const getEditorColor = (colorName: string): EditorColors => {
        switch (colorName) {
            case 'blue':
                return {
                    border: token['blue-6'],
                    background: token['blue-6'] + opacity,
                    selectedBorder: token['blue-9'],
                    selectedBackground: token['blue-9'] + opacity,
                };
            case 'gold':
                return {
                    border: token['gold-6'],
                    background: token['gold-6'] + opacity,
                    selectedBorder: token['gold-9'],
                    selectedBackground: token['gold-9'] + opacity,
                };
            case 'purple':
                return {
                    border: token['purple-6'],
                    background: token['purple-6'] + opacity,
                    selectedBorder: token['purple-9'],
                    selectedBackground: token['purple-9'] + opacity,
                };
            case 'yellow':
                return {
                    border: token['yellow-6'],
                    background: token['yellow-6'] + opacity,
                    selectedBorder: token['yellow-9'],
                    selectedBackground: token['yellow-9'] + opacity,
                };
            case 'cyan':
                return {
                    border: token['cyan-6'],
                    background: token['cyan-6'] + opacity,
                    selectedBorder: token['cyan-9'],
                    selectedBackground: token['cyan-9'] + opacity,
                };
            case 'green':
                return {
                    border: token['green-6'],
                    background: token['green-6'] + opacity,
                    selectedBorder: token['green-9'],
                    selectedBackground: token['green-9'] + opacity,
                };
            default:
                return {
                    border: token.colorBorder,
                    background: token.colorBgContainer,
                    selectedBorder: token.colorPrimary,
                    selectedBackground: token.colorPrimaryBg,
                };
        }
    };

    const editorColors: Record<string, EditorColors> = {
        container: getEditorColor(UIElementTypeColors.Containers),
        field: getEditorColor(UIElementTypeColors.Fields),
        entity: getEditorColor(UIElementTypeColors.Records),
        meta: getEditorColor(UIElementTypeColors['Meta Fields']),
        formProp: getEditorColor(UIElementTypeColors['Form Properties']),
        attachments: getEditorColor(UIElementTypeColors.Attachments)
    };

    /** Generates the main element container styles */
    const getElementStyle = ({
        isContainer,
        isColumn,
        isSelected,
        isMeta,
        isField,
        isFormProp,
        isAttachments,
        style,
        elementProps
    }: StyleProps): CSSProperties => ({
        display: 'flex',
        flexDirection: isContainer ? (isColumn ? 'column' : 'row') : 'row',
        justifyContent: isContainer ? undefined : 'flex-start',
        alignItems: isContainer ? 'flex-start' : 'center',
        flexGrow: isContainer ? 0 : 1,
        alignSelf: 'stretch',
        gap: 10,
        padding: isContainer ? '20px 20px 10px 10px' : '5px 5px 5px 10px',
        borderRadius: token.borderRadiusLG,
        minHeight: isContainer ? 50 : 30,
        minWidth: 120,
        position: 'relative',
        cursor: 'grab',
        border: `1px ${isContainer ? 'dotted' : 'dashed'} ${
            isSelected
                ? editorColors[isContainer ? 'container' : (isMeta ? 'meta' : (isField ? 'field' : (isFormProp ? 'formProp' : (isAttachments ? 'attachments' : 'entity'))))].selectedBorder
                : editorColors[isContainer ? 'container' : (isMeta ? 'meta' : (isField ? 'field' : (isFormProp ? 'formProp' : (isAttachments ? 'attachments' : 'entity'))))].border
        }`,
        backgroundColor: isSelected
            ? editorColors[isContainer ? 'container' : (isMeta ? 'meta' : (isField ? 'field' : (isFormProp ? 'formProp' : (isAttachments ? 'attachments' : 'entity'))))].selectedBackground
            : editorColors[isContainer ? 'container' : (isMeta ? 'meta' : (isField ? 'field' : (isFormProp ? 'formProp' : (isAttachments ? 'attachments' : 'entity'))))].background,
        ...style,
        ...(elementProps?.style || {})
    });

    /** Generates styles for the element type label */
    const getTypeTextStyle = (): CSSProperties => ({
        fontSize: token.fontSizeSM,
        position: 'absolute',
        left: 10,
        top: -2
    });

    /** Generates styles for the meta field icon */
    const getMetaIconStyle = (): CSSProperties => ({
        marginLeft: 8,
        color: token.colorTextSecondary
    });

    /** Generates styles for the drag handle icon */
    const getDragHandleStyle = (isContainer: boolean, isMeta: boolean, isField: boolean): CSSProperties => ({
        color: editorColors[isContainer ? 'container' : (isMeta ? 'meta' : (isField ? 'field' : 'entity'))].border,
        position: 'absolute',
        right: 2,
        top: '50%',
        transform: 'translateY(-50%)'
    });

    return {
        getElementStyle,
        getTypeTextStyle,
        getMetaIconStyle,
        getDragHandleStyle,
        editorColors
    };
}; 